import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, Search, Edit2, Trash2, Download } from 'lucide-react';
import Modal from '../components/Modal';
import { tenantsService, invoicesService } from '../services/supabaseService';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useSessionStorage('tenants_searchTerm', '');
  const [isModalOpen, setIsModalOpen] = useSessionStorage('tenants_isModalOpen', false);
  const [editingTenant, setEditingTenant] = useSessionStorage('tenants_editingTenant', null);
  const [statusFilter, setStatusFilter] = useSessionStorage('tenants_statusFilter', 'all');

  const [exportMonth, setExportMonth] = useSessionStorage('tenants_exportMonth', '');

  // Form State
  const [formData, setFormData] = useSessionStorage('tenants_formData', {
    name: '', email: '', aadhaar: '', mobile: '', room_no: '', room_type: '2 Sharing', monthly_rent: '', security_deposit: '', maintenance_fees: '', date_of_joining: '', floor: '1st Floor', vacated: false, vacated_date: '', reversed_deposit: false
  });

  const fetchTenants = async () => {
    try {
      const data = await tenantsService.getAll();
      let invData = [];
      try {
        invData = await invoicesService.getAll();
      } catch (invErr) {
        console.warn('Could not fetch invoices for tenant status:', invErr);
      }

      const updatedTenants = (Array.isArray(data) ? data : []).map(t => {
        const tInvoices = Array.isArray(invData) ? invData.filter(i => (i.name || '').toLowerCase() === (t.name || '').toLowerCase() && i.room_no === t.room_no) : [];
        tInvoices.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
        return {
          ...t,
          invoice_status: tInvoices.length > 0 ? tInvoices[0].status : null
        };
      });

      setTenants(updatedTenants);
    } catch (err) {
      console.error('Error fetching tenants/invoices:', err);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleOpenModal = (tenant = null) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        name: tenant.name || '',
        email: tenant.email || '',
        aadhaar: tenant.aadhaar || '',
        mobile: tenant.mobile || '',
        room_no: tenant.room_no || '',
        room_type: tenant.room_type || '2 Sharing',
        monthly_rent: tenant.monthly_rent || '',
        security_deposit: tenant.security_deposit || '',
        maintenance_fees: tenant.maintenance_fees || '',
        date_of_joining: tenant.date_of_joining ? tenant.date_of_joining.split('T')[0] : '',
        floor: tenant.floor || '1st Floor',
        vacated: Boolean(tenant.vacated),
        vacated_date: tenant.vacated_date ? tenant.vacated_date.split('T')[0] : '',
        reversed_deposit: Boolean(tenant.reversed_deposit)
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setEditingTenant(null);
      setFormData({
        name: '', email: '', aadhaar: '', mobile: '', room_no: '', room_type: '2 Sharing', monthly_rent: '', security_deposit: '', maintenance_fees: '', date_of_joining: today, floor: '1st Floor', vacated: false, vacated_date: '', reversed_deposit: false
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await tenantsService.update(editingTenant.id, formData);
      } else {
        await tenantsService.create(formData);
      }
      setIsModalOpen(false);
      fetchTenants();
    } catch (err) {
      console.error('Error saving tenant:', err);
      alert('Error saving tenant: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tenant record?')) {
      try {
        await tenantsService.delete(id);
        fetchTenants();
      } catch (err) {
        console.error('Error deleting tenant:', err);
        alert('Error deleting tenant: ' + err.message);
      }
    }
  };

  const handleExport = () => {
    const rows = [
      ['Tenant Name', 'Room No', 'Room Type', 'Floor', 'Mobile', 'Email', 'Aadhaar', 'Monthly Rent', 'Security Deposit', 'Maintenance Fee', 'Join Date', 'Vacated Status']
    ];
    let toExport = filteredTenants;
    if (exportMonth) {
      toExport = filteredTenants.filter(t => {
        const joinMonth = t.date_of_joining ? t.date_of_joining.substring(0, 7) : '';
        return joinMonth === exportMonth || (!t.vacated && joinMonth <= exportMonth);
      });
    }

    toExport.forEach(t => {
      rows.push([
        t.name, t.room_no, t.room_type, t.floor || '', t.mobile, t.email || '', t.aadhaar || '',
        t.monthly_rent, t.security_deposit || 0, t.maintenance_fees || 0,
        t.date_of_joining ? t.date_of_joining.split('T')[0] : '', t.vacated ? `Vacated (${t.vacated_date ? t.vacated_date.split('T')[0] : 'N/A'})` : 'Occupied'
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Tenants_Export_${exportMonth || 'All'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Extract available months from tenants' date_of_joining
  const availableMonths = React.useMemo(() => {
    const months = new Set();
    tenants.forEach(t => {
      if (t.date_of_joining) {
        const yyyymm = t.date_of_joining.substring(0, 7); // e.g. "2026-05"
        months.add(yyyymm);
      }
    });
    // Convert to Array and sort descending
    return ['All', ...Array.from(months).sort().reverse()];
  }, [tenants]);

  const formatMonth = (yyyymm) => {
    if (yyyymm === 'All') return 'All Months';
    const [year, month] = yyyymm.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = (tenant.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (tenant.room_no || '').includes(searchTerm) ||
                          (tenant.mobile || '').includes(searchTerm);
    if (!matchesSearch) return false;
    
    // Status Filter
    if (statusFilter !== 'all') {
      const isOccupied = !tenant.vacated;
      if (statusFilter === 'occupied' && !isOccupied) return false;
      if (statusFilter === 'vacant' && isOccupied) return false;
    }

    // Month Filter (filters by tenant activity during selected month)
    if (exportMonth) {
      const joinMonth = tenant.date_of_joining ? tenant.date_of_joining.substring(0, 7) : '';
      const vacateMonth = tenant.vacated_date ? tenant.vacated_date.substring(0, 7) : '';
      const joinedBeforeOrInMonth = joinMonth <= exportMonth;
      const notVacatedOrVacatedAfterMonth = !tenant.vacated || vacateMonth >= exportMonth;
      if (!(joinedBeforeOrInMonth && notVacatedOrVacatedAfterMonth)) return false;
    }

    return true;
  });

  const [currentPage, setCurrentPage] = useSessionStorage('tenants_currentPage', 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, exportMonth]);

  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage) || 1;
  const paginatedTenants = filteredTenants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const roomMap = tenants.reduce((acc, tenant) => {
    if (!tenant.vacated && tenant.room_no) {
      if (!acc[tenant.room_no]) acc[tenant.room_no] = [];
      acc[tenant.room_no].push(tenant);
    }
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <h2>Tenants Management</h2>
          <p>Manage all PG residents, room assignments, and records.</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <select
            value={exportMonth}
            onChange={(e) => setExportMonth(e.target.value)}
            style={{ 
              padding: '0.6rem 1rem', 
              borderRadius: '0.5rem', 
              border: '1px solid var(--border-color)', 
              backgroundColor: 'rgba(0,0,0,0.2)', 
              color: 'var(--text-primary)',
              cursor: 'pointer',
              outline: 'none',
              fontSize: '0.875rem'
            }}
            title="Filter by Joining/Active Month"
          >
            {availableMonths.map(m => (
              <option key={m} value={m === 'All' ? '' : m} style={{ color: '#000' }}>
                {formatMonth(m)}
              </option>
            ))}
          </select>
          <button 
            className="flex items-center gap-2" 
            onClick={handleExport} 
            style={{ 
              backgroundColor: 'var(--surface-highlight)', 
              color: 'var(--text-primary)',
              padding: '0.6rem 1.2rem',
              borderRadius: '0.5rem',
              border: '1px solid var(--border-color)',
              fontWeight: '500',
              transition: 'all 0.2s',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <Download size={16} /> Export
          </button>
          <button 
            className="btn-primary flex items-center gap-2" 
            onClick={() => handleOpenModal()}
            style={{
              padding: '0.6rem 1.2rem',
              borderRadius: '0.5rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <Plus size={16} /> Add New Tenant
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <h3 className="mb-4">Active Room Layout</h3>
        {Object.keys(roomMap).length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No active rooms currently.</p>
        ) : (
          <div className="grid grid-cols-4 gap-6">
            {Object.keys(roomMap).sort().map(room => (
              <div key={room} className="card glass-panel" style={{ borderTop: '4px solid var(--primary-color)', padding: '1.25rem' }}>
                <div className="flex justify-between items-center mb-4">
                  <div className="card-title" style={{ margin: 0 }}>Room {room}</div>
                  <div style={{ fontSize: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary-color)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontWeight: '600' }}>
                    {roomMap[room].length} Occupants
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {roomMap[room].map(t => (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--accent-color)' }}></span>
                      {t.name} ({t.room_type})
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="relative flex-1" style={{ minWidth: '280px', maxWidth: '400px' }}>
            <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              type="text"
              placeholder="Search by name, room or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem', width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: 'rgba(0,0,0,0.2)', padding: '0.25rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            {['all', 'occupied', 'vacant'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  backgroundColor: statusFilter === status ? 'var(--primary-color)' : 'transparent',
                  color: statusFilter === status ? '#fff' : 'var(--text-secondary)',
                  fontWeight: statusFilter === status ? '600' : '500',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s'
                }}
              >
                {status === 'all' ? 'All Residents' : status === 'occupied' ? 'Active (Occupied)' : 'Past (Vacated)'}
              </button>
            ))}
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Tenant Name</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Room & Floor</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Contact & Aadhaar</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Rent (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Deposit (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Maint. (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTenants.map((tenant) => (
                <tr key={tenant.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '1rem 0' }}>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{tenant.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      Joined {tenant.date_of_joining ? tenant.date_of_joining.split('T')[0] : ''}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0' }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Room {tenant.room_no} ({tenant.room_type})</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{tenant.floor}</div>
                  </td>
                  <td style={{ padding: '1rem 0' }}>
                    <div>{tenant.mobile}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{tenant.email} {tenant.aadhaar ? `| Aadhaar: ${tenant.aadhaar}` : ''}</div>
                  </td>
                  <td style={{ padding: '1rem 0', fontWeight: '500' }}>{tenant.monthly_rent}</td>
                  <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>{tenant.security_deposit || 0}</td>
                  <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>{tenant.maintenance_fees || 0}</td>
                  <td style={{ padding: '1rem 0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: !tenant.vacated ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: !tenant.vacated ? 'var(--accent-color)' : 'var(--danger-color)'
                      }}>
                        {!tenant.vacated ? 'Occupied' : `Vacated ${tenant.vacated_date ? `(${tenant.vacated_date.split('T')[0]})` : ''}`}
                      </span>
                      {Boolean(tenant.reversed_deposit) && (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6'
                        }}>
                          Deposit Refunded
                        </span>
                      )}
                      {tenant.invoice_status && (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: tenant.invoice_status === 'Paid' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          color: tenant.invoice_status === 'Paid' ? 'var(--accent-color)' : 'var(--danger-color)'
                        }}>
                          Inv: {tenant.invoice_status.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0', textAlign: 'right' }}>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleOpenModal(tenant)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--text-secondary)' }}>
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(tenant.id)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--danger-color)' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredTenants.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No tenants found matching your search.
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredTenants.length)} of {filteredTenants.length} entries
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingTenant ? "Edit Tenant" : "Add New Tenant"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Full Name</label>
            <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Aarohi Sharma" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Room No</label>
              <input required type="text" value={formData.room_no} onChange={e => setFormData({ ...formData, room_no: e.target.value })} placeholder="e.g. 101" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Room Type</label>
              <select
                value={formData.room_type}
                onChange={e => setFormData({ ...formData, room_type: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Single" style={{ color: '#000' }}>Single Occupancy</option>
                <option value="2 Sharing" style={{ color: '#000' }}>2 Sharing</option>
                <option value="3 Sharing" style={{ color: '#000' }}>3 Sharing</option>
                <option value="4 Sharing" style={{ color: '#000' }}>4 Sharing</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Floor</label>
              <select
                value={formData.floor}
                onChange={e => setFormData({ ...formData, floor: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Ground Floor" style={{ color: '#000' }}>Ground Floor</option>
                <option value="1st Floor" style={{ color: '#000' }}>1st Floor</option>
                <option value="2nd Floor" style={{ color: '#000' }}>2nd Floor</option>
                <option value="3rd Floor" style={{ color: '#000' }}>3rd Floor</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Email</label>
              <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="tenant@example.com" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Mobile No</label>
              <input required type="text" value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} placeholder="9876543210" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Aadhaar No</label>
              <input type="text" maxLength={12} value={formData.aadhaar} onChange={e => setFormData({ ...formData, aadhaar: e.target.value })} placeholder="12-digit number" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Monthly Rent (₹)</label>
              <input required type="number" value={formData.monthly_rent} onChange={e => setFormData({ ...formData, monthly_rent: e.target.value })} placeholder="8000" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Security Deposit (₹)</label>
              <input type="number" value={formData.security_deposit} onChange={e => setFormData({ ...formData, security_deposit: e.target.value })} placeholder="5000" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Maintenance Fee (₹)</label>
              <input type="number" value={formData.maintenance_fees} onChange={e => setFormData({ ...formData, maintenance_fees: e.target.value })} placeholder="1000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Date of Joining</label>
              <input required type="date" value={formData.date_of_joining} onChange={e => setFormData({ ...formData, date_of_joining: e.target.value })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingTop: '1.5rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={formData.vacated} onChange={e => setFormData({ ...formData, vacated: e.target.checked })} style={{ width: '1.25rem', height: '1.25rem' }} />
                Vacated Room
              </label>
              {formData.vacated && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Date:</span>
                  <input type="date" value={formData.vacated_date} onChange={e => setFormData({ ...formData, vacated_date: e.target.value })} style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)' }} />
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={formData.reversed_deposit} onChange={e => setFormData({ ...formData, reversed_deposit: e.target.checked })} style={{ width: '1.25rem', height: '1.25rem' }} />
                Deposit Refunded
              </label>
            </div>
          </div>
          <button type="submit" className="btn-primary mt-4">
            {editingTenant ? 'Update Tenant Record' : 'Save Tenant Record'}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default Tenants;
